import { Stack } from "@mui/material"
import { MutationFunction, useMutation } from "@tanstack/react-query"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { NexusSuccessResponse } from "../../api/API"
import { StepperForm } from "../../components/StepperForm/StepperForm"
import { Title } from "../../components/Typography/Typography"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import { useTitle } from "../../hooks/useTitle"
import { ConfirmationRoutes } from "../../routing/NexRoutes"
import { useDataReceiptStore } from "../../zstore/DataReceiptStore"
import { ReceiptDetailDialog } from "./ReceiptDetailDialog"
import { ReceiptDetails } from "./ReceiptDetails"
import { SelectMissions } from "./SelectMissions"
import { SelectedMissionsList } from "./SelectedMissionsList"
import { getCreateDataReceiptModel } from "./model"

interface DataReceiptEditorProps {
    mutationFn: MutationFunction<NexusSuccessResponse<unknown>, void> | undefined
    confirmationRoute: ConfirmationRoutes
    pageTitle: string
}

export const DataReceiptEditor = (props: DataReceiptEditorProps) => {
    const navigate = useNavigate()
    const nexusNav = useNexusNavigate()
    const { mutationFn, confirmationRoute, pageTitle } = props
    useTitle(pageTitle)

    const [errorMessage, setErrorMessage] = useState<string>()

    const receiptDetailsForm = useDataReceiptStore((state) => state.receiptDetailsForm)
    const isFetchingDataReceiptFormData = useDataReceiptStore((state) => state.isFetchingDataReceiptFormData)
    const selectedShippingInfoWrappers = useDataReceiptStore((state) => state.selectedShippingInfoWrappers)
    const validDataReceipt = useDataReceiptStore((state) => state.validDataReceipt)
    const clearDataReceiptState = useDataReceiptStore((state) => state.clearState)

    const useDataReceiptMutation = useMutation({
        mutationFn,
        onSuccess: () => {
            nexusNav.confirmationPage(confirmationRoute)
            clearDataReceiptState()
        },
        onError: (error) => {
            setErrorMessage(error.message)
        },
    })

    const handleStepChange = async (newStep: number) => {
        if (newStep === -1) {
            // on first page -> go back
            navigate(-1)
        } else {
            // submit
            useDataReceiptMutation.mutate()
        }
    }

    return (
        <StepperForm
            header={<Title>{pageTitle}</Title>}
            onStepChange={handleStepChange}
            disableSubmit={!validDataReceipt}
            errorMessages={[errorMessage]}
            submitting={useDataReceiptMutation.isPending}
            loading={isFetchingDataReceiptFormData}
            validationProps={{
                model: getCreateDataReceiptModel(receiptDetailsForm, selectedShippingInfoWrappers),
                dependencies: [
                    receiptDetailsForm.office,
                    receiptDetailsForm.date_received,
                    receiptDetailsForm.received_by,
                    selectedShippingInfoWrappers,
                ],
            }}
        >
            <Stack spacing={4} sx={{ pb: 4 }}>
                <ReceiptDetails />

                {/* Accordion to Add Missions */}
                <SelectMissions />

                {/* The Selected Missions Table */}
                <SelectedMissionsList />

                {/* Receipt Dialog */}
                <ReceiptDetailDialog />
            </Stack>
        </StepperForm>
    )
}
